import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';

/**
 * Mini-weergave van de PHS-kleur: een horizontaal-gesplitste vorm met het
 * kleurcijfer 1-6. Style hangt af van {@link HdPlanetGridColorConfig.beneathTheLineStyle}:
 *  - `'classic'` → hexagon (flat-top) met border;
 *  - andere → halve cirkel (geen border, modern look).
 *
 * Kleur-regels (horizontale split):
 *  - 1-3 → top = primary, bottom = side-color;
 *  - 4-6 → top = side-color, bottom = primary.
 *
 * Geëxtraheerd uit {@link HdColorToneBlockComponent} zodat de PHS-info-panel
 * dezelfde rendering kan hergebruiken (visuele consistentie tussen chart en
 * info-dialog).
 */
@Component({
  selector: 'hd-color-shape',
  templateUrl: './hd-color-shape.component.html',
  styleUrl: './hd-color-shape.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdColorShapeComponent {
  readonly colorNumber = input.required<number>();
  readonly side = input.required<HdBodygraphSide>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();

  protected readonly isClassicStyle = computed(() => this.colorConfig().beneathTheLineStyle === 'classic');

  protected readonly sideColor = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.side() === 'design' ? shape.design : shape.personality;
  });

  protected readonly primaryColor = computed(() => this.colorConfig().colorToneShape.primary);

  /** 1-3 = bovenhelft primary, 4-6 = bovenhelft side-color. */
  protected readonly topFill = computed(() =>
    this.isLowerHalf(this.colorNumber()) ? this.primaryColor() : this.sideColor(),
  );
  protected readonly bottomFill = computed(() =>
    this.isLowerHalf(this.colorNumber()) ? this.sideColor() : this.primaryColor(),
  );

  protected readonly numberColor = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.isClassicStyle() ? shape.classicNumberColor : shape.numberColor;
  });

  /** Witte outline rond het cijfer in classic; geen outline in modern. */
  protected readonly numberTextShadow = computed(() => {
    if (!this.isClassicStyle()) return 'none';
    const outline = this.colorConfig().colorToneShape.classicNumberOutlineColor;
    return `-1px -1px 0 ${outline}, 1px -1px 0 ${outline}, -1px 1px 0 ${outline}, 1px 1px 0 ${outline}`;
  });

  protected readonly borderColor = computed(() =>
    this.isClassicStyle() ? this.colorConfig().colorToneShape.borderColor : 'none',
  );

  private isLowerHalf(value: number): boolean {
    return value >= 1 && value <= 3;
  }
}
